import React from 'react';
import Route from '../Route';
import { resolve } from '../../services/resolver';
import queryString from '../../utilities/queryString';
import { parse as parseQueryString } from 'querystring';

import Settings from './Settings.jsx';
import Channels from './Channels';
import NewChannel from './NewChannel.jsx';
import Account from './Account.jsx';
import CustomFields from './CustomFields.jsx';
import Integrations from './Integrations';
import LegacyIntegration from './LegacyIntegration';
import Integration from './Integration';
import DynamicsOAuth from './DynamicsOAuth.jsx';
import TwitterOAuth from './TwitterOAuth.jsx';
import Tags from './Tags';
import SavedReplies from './SavedReplies';
import ImportFailures from './ImportFailures';
import GeckoRequest from '../../services/gecko-request';
import { renderError } from 'services/gecko-error';
import { SalesForceConfirmationDialog } from './components/SalesforceConfirmationModal';
import { GeckoToastService } from '../../ui/services';
import { Exports } from './Exports';
import NewExport from './Exports/NewExport';
import DataSources from './DataSources';
import DataSource from './DataSource';
import NotFound from 'routes/404/NotFound';
import GeckoFeatures from 'services/gecko-features';
import TeamsIntegration from './LegacyIntegration/TeamsIntegration';
import SlackIntegration from './LegacyIntegration/SlackIntegration';
import { UpdateExport } from './Exports/UpdateExport';

const dataSourceCheck =
  (action) =>
  (...args) => {
    const dataSourceEnabled = GeckoFeatures.can('data-source');
    if (!dataSourceEnabled) {
      return <NotFound />;
    }
    return action(...args);
  };

const getParams = (_) =>
  window.location.href
    .split('&')
    .filter((p) => p !== '')
    .map((part) => ({
      [part.substring(0, part.indexOf('='))]: part.substring(
        part.indexOf('=') + 1
      ),
    }))
    .reduce((obj, item) => Object.assign({}, obj, item), {});

const routes = new Route({
  path: '/settings',
  children: [
    {
      name: 'Settings - Channels',
      path: '/channels',
      children: [
        {
          name: 'Settings - Channels',
          path: '/',
          requires: ['admin', 'supervisor'],
          action: () => (
            <Settings>
              <Channels />
            </Settings>
          ),
        },
        {
          name: 'Settings - New Channel',
          path: '/new',
          requires: ['admin', 'supervisor'],
          action: () => (
            <Settings>
              <NewChannel />
            </Settings>
          ),
        },
      ],
    },
    {
      name: 'Settings - Account',
      path: '/account',
      requires: ['admin'],
      action: async function (context) {
        return (
          <Settings>
            <Account />
          </Settings>
        );
      },
    },
    {
      name: 'Settings - Custom Fields',
      path: '/customfields',
      requires: ['admin', 'supervisor'],
      action: () => (
        <Settings>
          <CustomFields />
        </Settings>
      ),
    },
    {
      name: 'Settings - Integrations',
      path: '/integrations',
      requires: ['admin', 'supervisor'],
      action: async function (context) {
        if (getParams().setup === 'failed') {
          new GeckoToastService().error(
            'Integration failed. Please try again. If the issues persists please contact Support'
          );
        }
        return (
          <Settings>
            <Integrations />
          </Settings>
        );
      },
    },
    {
      name: 'Settings - Integration - Radius Import Failures',
      path: '/integrations/callbacks/oauth',
      requires: ['admin', 'supervisor'],
      action: () => {
        const { code } = parseQueryString(window.location.search.slice(1));
        window.opener.postMessage(
          JSON.stringify({ type: 'oauth-callback', code }),
          process.env.REACT_APP_APP_URL
        );
        return null;
      },
    },
    {
      name: 'Settings - Integration - Radius Import Failures',
      path: '/integrations/import-failures/:parentSyncId',
      requires: ['admin', 'supervisor'],
      action: ({ params }) => {
        return (
          <Settings>
            <ImportFailures parentSyncId={params.parentSyncId} />
          </Settings>
        );
      },
    },
    {
      name: 'Settings - Teams Integration',
      path: '/integration/webhooks/teams',
      resolves: {
        teamsIntegrations: () => {
          return GeckoRequest.get('/integration/teams').catch(renderError);
        },
      },
      requires: ['admin', 'supervisor'],
      action: async () => {
        return (
          <Settings>
            <TeamsIntegration />
          </Settings>
        );
      },
    },
    {
      name: 'Settings - Slack Integration',
      path: '/integration/webhooks/slack',
      resolves: {
        teamsIntegrations: () => {
          return GeckoRequest.get('/integration/slack').catch(renderError);
        },
      },
      requires: ['admin', 'supervisor'],
      action: async () => {
        return (
          <Settings>
            <SlackIntegration />
          </Settings>
        );
      },
    },
    {
      name: 'Settings - Integration',
      path: '/integrations/:integrationId',
      resolves: {},
      requires: ['admin', 'supervisor'],
      action: async function ({ params }) {
        return (
          <Settings>
            <Integration integrationId={params.integrationId} />
          </Settings>
        );
      },
    },
    {
      name: 'Settings - Integration',
      path: '/integration/:type',
      resolves: {
        crmFields: ({ params }) => {
          return GeckoRequest.get(`/integration/${params.type}/fields`).catch(
            () => {
              return renderError({
                safe: [
                  `Unable to get fields from ${
                    params.type.charAt(0).toUpperCase() + params.type.slice(1)
                  } API`,
                ],
              });
            }
          );
        },
        crmModules: ({ params }) => {
          return GeckoRequest.get(`/integration/${params.type}/modules`).catch(
            () => {
              return renderError({
                safe: [
                  `Unable to get modules from ${
                    params.type.charAt(0).toUpperCase() + params.type.slice(1)
                  } API`,
                ],
              });
            }
          );
        },
        targetOptions: ({ params }) => {
          if (['radius', 'dynamics'].includes(params.type)) {
            return GeckoRequest.get(
              `/integration/${params.type}/targets`
            ).catch(() => {
              return renderError({
                safe: [
                  `Unable to get targets from ${
                    params.type.charAt(0).toUpperCase() + params.type.slice(1)
                  } API`,
                ],
              });
            });
          }
          return [];
        },
        lastSync: ({ params }) => {
          if (['radius', 'dynamics'].includes(params.type)) {
            return GeckoRequest.get(
              `/integration/${params.type}/last_sync`
            ).catch(() => {
              return renderError({
                safe: [
                  `Unable to get last sync from ${
                    params.type.charAt(0).toUpperCase() + params.type.slice(1)
                  } API`,
                ],
              });
            });
          }
          return null;
        },
      },
      requires: ['admin', 'supervisor'],
      action: async function (context) {
        const { crmFields, crmModules, integration, targetOptions, lastSync } =
          await resolve(this.resolves, context);
        return (
          <Settings>
            <LegacyIntegration
              crmFields={crmFields}
              crmModules={crmModules}
              integrationType={context.params?.type}
              targetOptions={targetOptions}
              lastSync={lastSync}
            />
          </Settings>
        );
      },
    },

    /*
     * The following routes are for OAuth2 callbacks from Channels e.g. Twitter
     */
    {
      name: 'Settings - Twitter OAuth',
      path: '/channels/callbacks/twitter',
      resolves: {},
      requires: ['admin', 'supervisor'],
      action: async function (context) {
        const url = window.location.href;
        const urlParams = url.substring(
          url.indexOf('/channels/callbacks/twitter') + 27,
          url.length
        );
        const { user_id } = queryString.parse(urlParams);
        return (
          <Settings>
            <TwitterOAuth twitterId={user_id} />
          </Settings>
        );
      },
    },
    /*
     * The following routes are for OAuth2 callbacks from Integrations e.g. Salesforce
     */
    {
      name: 'Settings - Dynamics OAuth',
      path: '/integrations/callbacks/dynamics',
      resolves: {},
      requires: ['admin', 'supervisor'],
      action: async function (context) {
        return (
          <Settings>
            <DynamicsOAuth />
          </Settings>
        );
      },
    },
    {
      name: 'Settings - Salesforce OAuth',
      path: '/integrations/callbacks/salesforce',
      resolves: {},
      requires: ['admin', 'supervisor'],
      action: async (_) => (
        <Settings>
          <SalesForceConfirmationDialog code={getParams().code} />
        </Settings>
      ),
    },

    {
      name: 'Settings - Tags',
      path: '/tags',
      requires: ['admin', 'supervisor'],
      action: () => (
        <Settings>
          <Tags />
        </Settings>
      ),
    },

    {
      name: 'Settings - Saved Replies',
      path: '/savedreplies',
      requires: ['admin', 'supervisor'],
      action: () => (
        <Settings>
          <SavedReplies />
        </Settings>
      ),
    },
    {
      name: 'Settings - Exports',
      path: '/exports',
      requires: ['admin', 'supervisor'],
      action: () => (
        <Settings>
          <Exports />
        </Settings>
      ),
    },
    {
      name: 'Settings - New Export',
      path: '/exports/new',
      requires: ['admin', 'supervisor'],
      action: () => (
        <Settings>
          <NewExport />
        </Settings>
      ),
    },
    {
      name: 'Settings - Data Sources',
      path: '/datasources',
      requires: ['admin', 'supervisor'],
      action: dataSourceCheck(() => (
        <Settings>
          <DataSources />
        </Settings>
      )),
    },
    {
      name: 'Settings - Update Export',
      path: '/exports/:exportId',
      requires: ['admin', 'supervisor'],
      action: ({ params }) => (
        <Settings>
          <UpdateExport exportId={params.exportId} />
        </Settings>
      ),
    },
    {
      name: 'Settings - Data Sources data',
      path: '/datasources/:dataSourceId',
      requires: ['admin', 'supervisor'],

      action: dataSourceCheck(({ params }) => {
        const { dataSourceId } = params;

        return (
          <Settings>
            <DataSource dataSourceId={dataSourceId} />{' '}
          </Settings>
        );
      }),
    },
  ],
});

export default routes;
